/**
 * healther-ui.js — shared client-side runtime for Healther OPD screens.
 *
 * Responsibilities:
 *  - Single language toggle (EN ↔ हिं) in top-right; persisted in localStorage,
 *    fired as the `healther:lang` CustomEvent so screens can re-render.
 *  - i18n helper t(key, lang?) reading from window.HEALTHER_I18N (each page
 *    registers its strings before invoking renderLangToggle()).
 *  - Inline SVG icons matching the design system's Lucide-style stroke.
 *
 * Load order on every patient screen:
 *   1. /frontend/opd-design-system/colors_and_type.css
 *   2. /frontend/shared/healther-ui.js
 */
(function (global) {
  const STORAGE_KEY = 'healther:lang';
  const DEFAULT_LANG = 'en';

  function getLang() {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG; } catch { return DEFAULT_LANG; }
  }
  function setLang(lang) {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
    document.documentElement.lang = lang;
    document.body.dataset.lang = lang;
    document.dispatchEvent(new CustomEvent('healther:lang', { detail: { lang } }));
    renderLangToggle();
  }

  function t(key, lang = getLang()) {
    const bundle = (global.HEALTHER_I18N && global.HEALTHER_I18N[lang]) || {};
    const fallback = (global.HEALTHER_I18N && global.HEALTHER_I18N.en) || {};
    return bundle[key] != null ? bundle[key] : (fallback[key] != null ? fallback[key] : key);
  }

  // Lucide-style icons (1.7 px stroke) used across screens. Each returns an
  // SVG string so it can be injected into innerHTML.
  const Icon = {
    mic: (s = 16) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>`,
    stop: (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14" rx="1.5"/></svg>`,
    sparkles: (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="currentColor" fill-opacity="0.45" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l1.9 4.6L19 9l-4.6 1.9L12 16l-1.9-4.6L5 9l4.6-1.9z"/></svg>`,
    arrowR: (s = 12) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`,
    arrowL: (s = 12) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`,
    check: (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
    plus: (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`,
    pencil: (s = 14) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>`,
    camera: (s = 16) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`,
    signature: (s = 16) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17.5c2-1 4-3 4-6 0-1.5-1-2.5-2-2.5-1.5 0-2.5 2-1.5 4.5S7 18.5 10 16s4-7 6-5"/><path d="M14 19h7"/></svg>`,
    download: (s = 16) => `<svg width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`,
  };

  function renderLangToggle() {
    let el = document.getElementById('healther-lang-toggle');
    const lang = getLang();
    if (!el) {
      el = document.createElement('div');
      el.id = 'healther-lang-toggle';
      el.style.cssText = `
        position: fixed; top: 16px; right: 16px; z-index: 1000;
        display: inline-flex; padding: 3px; border-radius: 999px;
        background: rgba(255,255,255,0.96); border: 1px solid var(--border-subtle);
        box-shadow: 0 2px 8px rgba(15,31,44,0.08);
      `;
      document.body.appendChild(el);
    }
    el.innerHTML = `
      <button data-lang="en" style="height:32px;padding:0 14px;border-radius:999px;border:0;cursor:pointer;font-weight:600;font-size:13px;background:${lang==='en'?'var(--primary-600)':'transparent'};color:${lang==='en'?'#fff':'var(--text-secondary)'};font-family:inherit;">EN</button>
      <button data-lang="hi" style="height:32px;padding:0 14px;border-radius:999px;border:0;cursor:pointer;font-weight:600;font-size:13px;background:${lang==='hi'?'var(--primary-600)':'transparent'};color:${lang==='hi'?'#fff':'var(--text-secondary)'};font-family:'Noto Sans Devanagari',inherit;">हिं</button>
    `;
    el.querySelectorAll('button').forEach((b) => {
      b.onclick = () => setLang(b.dataset.lang);
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[<>&"']/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Inject base styles every patient/doctor page can rely on.
  function injectBaseStyles() {
    if (document.getElementById('healther-base-style')) return;
    const css = `
      @keyframes healther-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .55; transform: scale(1.08); } }
      @keyframes healther-spin { to { transform: rotate(360deg); } }
      .h-spinner { width:18px; height:18px; border:2px solid var(--border-subtle); border-top-color: var(--primary-600); border-radius:50%; animation: healther-spin .9s linear infinite; }
      body[data-lang="hi"], body[data-lang="hi"] textarea, body[data-lang="hi"] input { font-family: "Noto Sans Devanagari","Inter",sans-serif; }
      .h-card { background: var(--bg-surface); border: 1px solid var(--border-subtle); border-radius: 14px; padding: 20px; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(15,31,44,0.06); }
      .h-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; min-height: 48px; padding: 0 18px; border-radius: 12px; font-size: 15px; font-weight: 600; border: 0; cursor: pointer; font-family: inherit; }
      .h-btn-primary { background: var(--primary-600); color: #fff; }
      .h-btn-primary:hover { background: var(--primary-700); }
      .h-btn-primary:disabled { background: var(--text-disabled); cursor: not-allowed; }
      .h-btn-secondary { background: transparent; border: 1px solid var(--border-default); color: var(--text-primary); }
      .h-btn-secondary:hover { background: var(--bg-subtle); }
      .h-link { color: var(--primary-600); text-decoration: none; font-weight: 500; }
      .h-link:hover { text-decoration: underline; }
      .h-mic { display:inline-flex; align-items:center; gap:10px; padding:14px 22px; border-radius: 999px; background: var(--bg-surface); border: 1px solid var(--border-default); color: var(--text-primary); cursor: pointer; font-size: 15px; font-weight: 600; font-family: inherit; min-height: 52px; }
      .h-mic.recording { background: var(--recording-legacy); color: white; border-color: var(--recording-legacy); }
      .h-mic .dot { width: 10px; height: 10px; border-radius: 50%; background: var(--recording-legacy); }
      .h-mic.recording .dot { background: white; animation: healther-pulse 1.2s ease-in-out infinite; }
      .h-input { width: 100%; padding: 12px 14px; font-size: 16px; line-height: 1.45; border: 1px solid var(--border-default); border-radius: 10px; background: var(--bg-sunken); color: var(--text-primary); box-sizing: border-box; font-family: inherit; }
      .h-input:focus { outline: none; border-color: var(--border-focus); box-shadow: 0 0 0 3px rgba(15,109,140,0.18); }
      .h-textarea { min-height: 110px; resize: vertical; }
      .h-label { display:block; font-size:12px; font-weight:600; text-transform:uppercase; color: var(--text-tertiary); letter-spacing: 0.04em; margin-bottom: 6px; }
      .h-ai-card { background: var(--ai-bg); border-left: 3px solid var(--ai-border); padding: 14px 16px; border-radius: 10px; margin-bottom: 12px; }
      .h-ai-card .h-ai-title { font-size: 11px; color: var(--ai-text); font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; display:inline-flex; gap:6px; align-items:center; }
      .h-progress { display:flex; gap:6px; margin-bottom: 18px; }
      .h-progress .pill { flex:1; height:6px; border-radius:999px; background: var(--bg-subtle); }
      .h-progress .pill.active { background: var(--primary-600); }
      .h-progress .pill.done { background: var(--verified-border); }
      .h-error { background: var(--critical-bg); border: 1px solid #F4B9B4; color: var(--critical-text); padding: 10px 12px; border-radius: 8px; font-size: 14px; }
      .h-success { background: var(--verified-bg); border: 1px solid var(--verified-border); color: var(--verified-text); padding: 10px 12px; border-radius: 8px; font-size: 14px; }
    `;
    const style = document.createElement('style');
    style.id = 'healther-base-style';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function init() {
    injectBaseStyles();
    document.documentElement.lang = getLang();
    document.body.dataset.lang = getLang();
    // Render toggle after document ready.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', renderLangToggle);
    } else {
      renderLangToggle();
    }
  }

  global.HealtherUI = { getLang, setLang, t, Icon, renderLangToggle, escapeHtml, init };

  if (typeof window !== 'undefined') init();
})(typeof window !== 'undefined' ? window : globalThis);
